/**
 * PhotoGallery Lightbox Component
 *
 * Full-screen photo gallery viewer with:
 * - Smooth crossfade transitions between images
 * - Keyboard navigation (arrow keys, escape)
 * - Touch/swipe support for mobile
 * - Thumbnail strip with scroll tracking
 * - Autoplay slideshow mode
 * - Counter display
 * - Adjacent image preloading
 * - Responsive layout for all screen sizes
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
// @ts-ignore
import { VibesitesImage } from '@vibesites/ui-primitives';
// @ts-ignore
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@vibesites/assets';

export interface GalleryPhoto {
  src: string;
  alt?: string;
}

interface PhotoGalleryProps {
  photos: GalleryPhoto[];
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
}

export function PhotoGallery({ photos, initialIndex = 0, open, onClose }: PhotoGalleryProps) {
  const [index, setIndex] = useState(initialIndex);
  const [prevIndex, setPrevIndex] = useState(initialIndex);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showThumbs, setShowThumbs] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);
  const transitionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const total = photos.length;

  // Sync initialIndex when lightbox opens
  useEffect(() => {
    if (open) {
      setIndex(initialIndex);
      setPrevIndex(initialIndex);
      setIsTransitioning(false);
      setIsClosing(false);
    }
  }, [open, initialIndex]);

  const goTo = useCallback((nextIdx: number) => {
    if (isTransitioning || nextIdx === index || total <= 1) return;
    setPrevIndex(index);
    setIndex(nextIdx);
    setIsTransitioning(true);
    if (transitionTimeout.current) clearTimeout(transitionTimeout.current);
    transitionTimeout.current = setTimeout(() => setIsTransitioning(false), 400);
  }, [index, isTransitioning, total]);

  const prev = useCallback(() => goTo((index - 1 + total) % total), [index, total, goTo]);
  const next = useCallback(() => goTo((index + 1) % total), [index, total, goTo]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => { if (transitionTimeout.current) clearTimeout(transitionTimeout.current); };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowLeft') {
        prev();
      } else if (e.key === 'ArrowRight') {
        next();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, prev, next]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.documentElement.style.overflow = '';
      };
    }
  }, [open]);

  // Scroll active thumbnail into view
  useEffect(() => {
    if (!showThumbs || !thumbsRef.current) return;
    const activeThumb = thumbsRef.current.children[index] as HTMLElement | undefined;
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [index, showThumbs]);

  // Swipe gesture support
  const touchStart = useRef<{ x: number; y: number; time: number } | null>(null);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() };
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    const dt = Date.now() - touchStart.current.time;
    touchStart.current = null;
    // Require minimum horizontal movement, and more horizontal than vertical
    if (Math.abs(dx) < 30 || Math.abs(dy) > Math.abs(dx)) {
      // Vertical swipe down to close
      if (dy > 80 && Math.abs(dy) > Math.abs(dx) && dt < 400) {
        handleClose();
      }
      return;
    }
    if (dx < 0) next();
    else prev();
  }, [next, prev]);

  // Preload adjacent images
  const adjacentSrcs = useMemo(() => {
    if (!open || total <= 1) return [];
    return [
      photos[(index + 1) % total]?.src,
      photos[(index - 1 + total) % total]?.src,
    ].filter(Boolean);
  }, [open, index, total, photos]);

  useEffect(() => {
    adjacentSrcs.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [adjacentSrcs]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 250);
  }, [onClose]);

  if (!open || total === 0) return null;

  const photo = photos[index];

  return (
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-[9999] flex flex-col select-none ${isClosing ? 'animate-[lightboxFadeOut_0.25s_ease-in_forwards]' : 'animate-[lightboxFadeIn_0.3s_ease-out_both]'}`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.92)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 z-10 shrink-0">
        <span className="text-white/70 text-sm font-medium tabular-nums">
          {index + 1} / {total}
        </span>
        <div className="flex items-center gap-2">
          {total > 1 && (
            <button
              onClick={() => setShowThumbs(s => !s)}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-150 border-none bg-transparent cursor-pointer"
              aria-label={showThumbs ? 'Hide thumbnails' : 'Show thumbnails'}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="6" height="6" rx="1" fill="currentColor" opacity={showThumbs ? 1 : 0.5} />
                <rect x="9.5" y="0.5" width="6" height="6" rx="1" fill="currentColor" opacity={showThumbs ? 1 : 0.5} />
                <rect x="0.5" y="9.5" width="6" height="6" rx="1" fill="currentColor" opacity={showThumbs ? 1 : 0.5} />
                <rect x="9.5" y="9.5" width="6" height="6" rx="1" fill="currentColor" opacity={showThumbs ? 1 : 0.5} />
              </svg>
            </button>
          )}
          <button
            onClick={handleClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-150 border-none bg-transparent cursor-pointer"
            aria-label="Close gallery"
          >
            <CloseIcon size={20} />
          </button>
        </div>
      </div>

      {/* Main stage — clicking empty area closes */}
      <div
        className="flex-1 relative flex items-center justify-center min-h-0 px-2 md:px-16"
        onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Navigation arrows */}
        {total > 1 && (
          <>
            <button
              className="hidden md:flex absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full items-center justify-center text-white/60 hover:text-white bg-white/5 hover:bg-white/15 backdrop-blur-sm transition-all duration-200 border-none cursor-pointer"
              onClick={prev}
              aria-label="Previous photo"
            >
              <ChevronLeftIcon size={22} />
            </button>
            <button
              className="hidden md:flex absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full items-center justify-center text-white/60 hover:text-white bg-white/5 hover:bg-white/15 backdrop-blur-sm transition-all duration-200 border-none cursor-pointer"
              onClick={next}
              aria-label="Next photo"
            >
              <ChevronRightIcon size={22} />
            </button>
          </>
        )}

        {/* Image with crossfade — clicking around (not on) the image closes */}
        <div
          className="relative w-full h-full flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          {/* Previous image (fading out) */}
          {isTransitioning && prevIndex !== index && (
            <div className="absolute inset-0 flex items-center justify-center animate-[lightboxImgFadeOut_0.4s_ease-out_forwards] pointer-events-none">
              <VibesitesImage
                url={photos[prevIndex]?.src || ''}
                alt=""
                className="max-w-full max-h-full object-contain"
                draggable={false}
                style={{ maxHeight: 'calc(100vh - 160px)' }}
              />
            </div>
          )}

          {/* Current image (fading in) */}
          <div className={`absolute inset-0 flex items-center justify-center ${isTransitioning ? 'animate-[lightboxImgFadeIn_0.4s_ease-out_both]' : ''}`}
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
          >
            <VibesitesImage
              url={photo.src}
              alt={photo.alt || ''}
              className="max-w-full max-h-full object-contain"
              draggable={false}
              style={{ maxHeight: 'calc(100vh - 160px)' }}
            />
          </div>
        </div>
      </div>

      {/* Thumbnail strip — animated show/hide */}
      {total > 1 && (
        <div
          className="shrink-0 px-4 z-10 overflow-hidden transition-all duration-300 ease-in-out"
          style={{
            maxHeight: showThumbs ? '80px' : '0px',
            paddingTop: showThumbs ? '12px' : '0px',
            paddingBottom: showThumbs ? '12px' : '0px',
            opacity: showThumbs ? 1 : 0,
          }}
        >
          <div
            ref={thumbsRef}
            className="flex items-center justify-center gap-2 overflow-x-auto pb-1"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}
          >
            {photos.map((p, i) => (
              <button
                key={i}
                className="shrink-0 rounded-md overflow-hidden border-2 bg-transparent p-0 cursor-pointer transition-all duration-200"
                style={{
                  width: i === index ? '64px' : '56px',
                  height: i === index ? '44px' : '38px',
                  borderColor: i === index ? 'white' : 'transparent',
                  opacity: i === index ? 1 : 0.5,
                }}
                onClick={() => goTo(i)}
                aria-label={`View photo ${i + 1}`}
              >
                <VibesitesImage
                  url={p.src}
                  alt=""
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes lightboxFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes lightboxFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes lightboxImgFadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes lightboxImgFadeOut {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(0.97); }
        }
        @media (prefers-reduced-motion: reduce) {
          [class*="animate-[lightbox"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

export default PhotoGallery;
